import React, { useEffect, useState, useMemo } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Fade from "@mui/material/Fade";
import Modal from "@mui/material/Modal";
import Paper from "@mui/material/Paper";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import TagComponent from "@meetyoo/components/src/components/Tag";
import { changeTicketTags, toggleTagModal } from "../../../../../actions/projectmail";

import { defaultPasscodeTheme } from "../../../../../constants/passCodeUtils";
import { spliceTag, originTag } from "../../../../../utils/objectMethods/tagOperations";

export const TagModalComponent = ({ changeTicketTagsFn, colorMap, close, rowId, tags, open, translate }) => {
  const theme = useTheme();
  const pc = useMemo(() => defaultPasscodeTheme(theme), [theme]);
  const s = useMemo(
    () => ({
      tag: {
        cursor: "pointer",
        pr: theme.spacing(0.5),
        pt: theme.spacing(0.5),
      },
      modal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      modalContainer: {
        width: "750px",
        backgroundColor: "#eee",
        border: "2px solid #000",
        boxShadow: theme.shadows[5],
        p: theme.spacing(2, 4, 3),
      },
      tagTitle: {
        fontSize: "24px",
        fontWeight: "bold",
      },
      inline: {
        display: "flex",
        maxWidth: "100%",
        flexWrap: "wrap",
      },
      container: { p: 2, minHeight: "96px" },
      bottomPadding: {
        pb: 1,
      },
      title: {
        fontSize: "16px",
        fontWeight: "bold",
      },
      paddingLeft: {
        pl: 2,
      },
    }),
    [theme]
  );
  const [addTag, setAddTag] = useState([]);
  const [removeTag, setRemoveTag] = useState([]);
  const convertedTags = useMemo(() => Object.keys(colorMap).map(tag => originTag(tag)), [colorMap]);

  const modifiedTag = (addableTag, setAddableTag, removeableTag, setRemoveableTag, tag) => {
    return (
      <Box
        aria-hidden
        key={spliceTag(tag)}
        role="button"
        sx={s.tag}
        onClick={() => {
          if (addableTag.indexOf(tag) === -1) setAddableTag(addableTag.concat([tag]));
          if (removeableTag.indexOf(tag) > -1) {
            removeableTag.splice(removeableTag.indexOf(tag), 1);
            setRemoveableTag(removeableTag);
          }
        }}
      >
        {[tag].map(() => {
          if (tag.charAt(0) === "#") {
            return <TagComponent key={tag} size="big" text={spliceTag(tag)} color={colorMap[spliceTag(tag)]} />;
          }
          return <TagComponent key={tag} size="big" text={tag} color={colorMap[tag]} />;
        })}
      </Box>
    );
  };

  return (
    open === true && (
      <Modal
        open={open}
        sx={s.modal}
        onClose={() => {
          close();
          setAddTag([]);
          setRemoveTag([]);
        }}
      >
        <Fade in={open}>
          <Box key="modalContainer" sx={s.modalContainer}>
            <Box sx={s.tagTitle}>{translate.tags}</Box>
            <Box sx={s.bottomPadding} />
            <Paper>
              <Box sx={s.container}>
                <Box sx={s.inline}>
                  <AddCircleIcon style={{ color: "rgb(0, 255, 0)" }} />
                  <Box sx={[s.bottomPadding, s.title]}>{translate.assigned}</Box>
                </Box>
                <Box sx={s.inline}>
                  {tags.map(tag => {
                    if (addTag.indexOf(tag) === -1 && removeTag.indexOf(tag) === -1)
                      return modifiedTag(removeTag, setRemoveTag, addTag, setAddTag, tag);
                    return null;
                  })}
                  {addTag.map(tag => modifiedTag(removeTag, setRemoveTag, addTag, setAddTag, tag))}
                </Box>
              </Box>
            </Paper>
            <Box sx={s.bottomPadding} />
            <Paper>
              <Box sx={s.container}>
                <Box sx={s.inline}>
                  <RemoveCircleIcon style={{ color: "rgb(255, 0, 0)" }} />
                  <Box sx={[s.bottomPadding, s.title]}>{translate.projectmail_add}</Box>
                </Box>
                <Box sx={s.inline}>
                  {convertedTags.map(tag => {
                    if (tags.indexOf(tag) === -1 && addTag.indexOf(tag) === -1)
                      return modifiedTag(addTag, setAddTag, removeTag, setRemoveTag, tag);
                    return null;
                  })}
                  {convertedTags.map(tag => {
                    if (removeTag.indexOf(tag) > -1 && tags.indexOf(tag) > -1)
                      return modifiedTag(addTag, setAddTag, removeTag, setRemoveTag, tag);
                    return null;
                  })}
                </Box>
              </Box>
            </Paper>
            <Box sx={[s.bottomPadding, pc.lineBreak]} />
            <Box sx={s.inline}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  close();
                  setAddTag([]);
                  setRemoveTag([]);
                }}
              >
                {translate.backButton}
              </Button>
              <Box sx={s.paddingLeft}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    const remove = removeTag.filter(tag => tags.indexOf(tag) !== -1);
                    if (remove.length !== 0) {
                      await changeTicketTagsFn(rowId, remove, true);
                    }
                    const add = addTag.filter(tag => tags.indexOf(tag) === -1);
                    if (add.length !== 0) {
                      await changeTicketTagsFn(rowId, add, false);
                    }
                    close();
                    setAddTag([]);
                    setRemoveTag([]);
                  }}
                >
                  {translate.saveButton}
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>
    )
  );
};

TagModalComponent.defaultProps = {
  tags: [],
  rowId: undefined,
};

TagModalComponent.propTypes = {
  changeTicketTagsFn: PropTypes.func.isRequired,
  colorMap: PropTypes.object.isRequired,
  rowId: PropTypes.number,
  close: PropTypes.func.isRequired,
  translate: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  tags: PropTypes.array,
};

const mapDispatchToProps = dispatch => ({
  changeTicketTagsFn: async (id, tags, del) => dispatch(changeTicketTags(id, tags, del)),
  close: () => dispatch(toggleTagModal(false)),
});

const mapStateToProps = state => ({
  colorMap: state.projectmail.colorMap,
  currentType: state.projectmail.currentType,
  translate: state.ui.translate,
  open: state.projectmail.tagModalOpen,
  tags: state.projectmail.currentRow ? state.projectmail.currentRow.tags : undefined,
  rowId: state.projectmail.currentRow ? state.projectmail.currentRow.id : undefined,
});

const TagModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(TagModalComponent);

export default TagModal;
